import { useEffect, useRef } from "react";
import SearchBar from "./SearchBar";

const HERO_IMAGE =
  "https://readdy.ai/api/search-image?query=Cinematic%20dusk%20photograph%20of%20a%20clean%20dark%20SUV%20driving%20along%20a%20winding%20tarmac%20road%20through%20the%20Kenyan%20highlands%20near%20Nairobi%2C%20warm%20amber%20sunset%20glow%2C%20dramatic%20clouds%2C%20deep%20moody%20shadows%2C%20premium%20automotive%20travel%20photography%2C%20wide%20angle%2C%20high%20detail&width=1600&height=900&seq=gari-hero-2026-a1&orientation=landscape";

const HERO_STATS = [
  { value: "10+", label: "Cars ready to book" },
  { value: "4.8", label: "Average rider rating" },
  { value: "3", label: "Cities across Kenya" },
  { value: "M-Pesa", label: "Secure local checkout" },
];

export default function HeroSection() {
  const imageRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let raf = 0;
    const onScroll = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        const node = imageRef.current;
        if (!node) return;
        const shifted = Math.min(window.scrollY, 700) * 0.22;
        node.style.transform = `translate3d(0, ${shifted}px, 0) scale(1.08)`;
      });
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", onScroll);
      cancelAnimationFrame(raf);
    };
  }, []);

  return (
    <section className="relative flex min-h-[660px] w-full items-center overflow-hidden md:min-h-[820px]">
      <div className="absolute inset-0">
        <div ref={imageRef} className="absolute inset-0 will-change-transform">
          <img
            src={HERO_IMAGE}
            alt="A car driving along a scenic road in the Kenyan highlands at dusk"
            title="Rent a car in Kenya with Gari — trusted local hosts"
            className="h-full w-full object-cover object-center"
          />
        </div>
        <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/50 to-black/65" />
        <div className="absolute inset-0 bg-gradient-to-r from-black/75 via-black/25 to-transparent" />
      </div>

      <div className="relative z-10 mx-auto w-full max-w-7xl px-4 pb-16 pt-28 md:px-8 md:pb-24 md:pt-40">
        <div className="max-w-3xl">
          <span className="animate-rise inline-flex items-center gap-2 rounded-full border border-background-200 bg-background-100/70 px-3.5 py-1.5 text-xs text-foreground-700 backdrop-blur">
            <i className="ri-map-pin-2-fill text-primary-500"></i>
            Kenya&apos;s peer-to-peer car marketplace
          </span>
          <h1
            className="animate-rise mt-5 font-heading text-4xl font-semibold leading-[1.05] text-white md:text-6xl"
            style={{ animationDelay: "120ms" }}
          >
            Rent the ride.
            <br />
            <span className="text-gradient-amber">Own the road.</span>
          </h1>
          <p
            className="animate-rise mt-5 max-w-xl text-base leading-relaxed text-white/75 md:text-lg"
            style={{ animationDelay: "240ms" }}
          >
            Book trusted cars from real Kenyan hosts — from a Fielder for the daily commute to a Prado for the Maasai
            Mara. Instant booking, transparent pricing, M-Pesa checkout.
          </p>
        </div>

        <div className="animate-rise mt-8 max-w-4xl" style={{ animationDelay: "360ms" }}>
          <SearchBar />
        </div>

        <div className="animate-rise mt-10 flex flex-wrap items-center gap-x-10 gap-y-5" style={{ animationDelay: "480ms" }}>
          {HERO_STATS.map((stat) => (
            <div key={stat.label}>
              <p className="font-heading text-2xl font-semibold text-white">{stat.value}</p>
              <p className="text-xs text-white/60">{stat.label}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}